import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { AnimatedSection } from "@/components/AnimatedSection"
import { FloatingCard } from "@/components/FloatingCard"
import { 
  Bot, 
  Clock,
  TrendingDown,
  Sparkles,
  Target,
  Zap,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  LineChart,
  Users,
  Calendar,
  ThumbsUp
} from "lucide-react"
import { useLocation } from "wouter"
import ReactGA from "react-ga4"

const isProduction = import.meta.env.PROD ||
  window.location.hostname === "socialmedia-a-team.com" ||
  window.location.hostname === "www.socialmedia-a-team.com";

const trackCtaClick = (buttonLocation: string) => {
  if (isProduction) {
    ReactGA.event({
      category: "engagement",
      action: "cta_click",
      label: buttonLocation
    });
  }
};

export default function PastorLanding() {
  const [, navigate] = useLocation()

  const handleGetStarted = (buttonLocation: string) => {
    trackCtaClick(buttonLocation);
    window.location.href = "/login"
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-background">
      {/* Navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-300">
        <div className="container flex h-16 items-center justify-between px-6 lg:px-8">
          <AnimatedSection direction="left" className="flex items-center space-x-2">
            <Bot className="h-8 w-8 text-primary transition-transform duration-300" />
            <span className="text-xl font-bold text-primary font-playfair">LinkedIn Lina</span>
          </AnimatedSection>
          <AnimatedSection direction="right">
            <Button 
              className="group transition-all duration-300"
              onClick={() => handleGetStarted("nav")}
              data-testid="button-nav-start"
            >
              Jetzt starten
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </AnimatedSection>
        </div>
      </header>

      {/* P - PROBLEM Section */}
      <section className="py-20 lg:py-32">
        <div className="container px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center space-y-6 mb-16">
              <Badge 
                variant="secondary" 
                className="w-fit mx-auto transition-transform duration-300"
                data-testid="badge-problem"
              >
                <AlertCircle className="h-4 w-4 mr-2 text-destructive" />
                Das Problem
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-balance font-playfair">
                LinkedIn Content erstellen ist{" "}
                <span className="text-destructive">zeitraubend</span> und{" "}
                <span className="text-destructive">inkonsistent</span>
              </h1>
              <p className="text-xl text-muted-foreground text-pretty max-w-3xl mx-auto">
                Du weißt, dass LinkedIn wichtig ist. Aber jeden Tag aufs Neue hochwertige Posts zu erstellen, 
                während du dein Business führst? Das ist ein echter Kampf.
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Clock,
                  title: "Zeitmangel",
                  description: "2-3 Stunden täglich für Content-Erstellung, die du nicht hast",
                  impact: "15+ Stunden/Woche verloren"
                },
                {
                  icon: TrendingDown,
                  title: "Inkonsistenz",
                  description: "Mal postest du, dann wieder wochenlang nichts - Zero Momentum",
                  impact: "Follower-Wachstum stoppt"
                },
                {
                  icon: Target,
                  title: "Rätselraten",
                  description: "Was funktioniert? Was nicht? Du weißt es einfach nicht.",
                  impact: "Keine Strategie"
                }
              ].map((problem, index) => (
                <AnimatedSection key={index} delay={index * 100} direction="up">
                  <FloatingCard className="h-full" data-testid={`problem-card-${index}`}>
                    <CardContent className="pt-6 space-y-4">
                      <problem.icon className="h-10 w-10 text-destructive" />
                      <h3 className="text-lg font-semibold">{problem.title}</h3>
                      <p className="text-sm text-muted-foreground">{problem.description}</p>
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs text-destructive font-medium">{problem.impact}</p>
                      </div>
                    </CardContent>
                  </FloatingCard>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* A - AMPLIFY Section */}
      <section className="py-20 bg-muted/30">
        <div className="container px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center space-y-6 mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-balance font-playfair">
                Und es wird jeden Tag{" "}
                <span className="text-destructive">schlimmer</span>
              </h2>
              <p className="text-xl text-muted-foreground text-pretty">
                Während du kämpfst, wächst deine Konkurrenz. Jeden Tag ohne konsistente Präsenz ist ein verlorener Tag.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={200} direction="up">
              <FloatingCard className="bg-destructive/5 border-destructive/20" data-testid="amplify-card">
                <CardContent className="pt-8 space-y-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-destructive" />
                        Was du verlierst
                      </h3>
                      <ul className="space-y-3">
                        {[
                          "Tausende potenzielle Leads sehen dich nie",
                          "Deine Expertise bleibt unsichtbar",
                          "Konkurrenten überholen dich",
                          "Netzwerk-Wachstum stagniert",
                          "Business-Chancen gehen verloren"
                        ].map((point, index) => (
                          <li key={index} className="flex items-start space-x-2 text-sm">
                            <TrendingDown className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Clock className="h-5 w-5 text-destructive" />
                        Was es dich kostet
                      </h3>
                      <div className="space-y-4">
                        <div className="p-4 bg-background rounded-lg border border-destructive/20">
                          <p className="text-3xl font-bold text-destructive">60+</p>
                          <p className="text-sm text-muted-foreground">Stunden monatlich für manuelles Posting</p>
                        </div>
                        <div className="p-4 bg-background rounded-lg border border-destructive/20">
                          <p className="text-3xl font-bold text-destructive">€0</p>
                          <p className="text-sm text-muted-foreground">ROI bei inkonsistentem Content</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </FloatingCard>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* S - STORY/SOLUTION Section */}
      <section className="py-20">
        <div className="container px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center space-y-6 mb-16">
              <Badge 
                variant="secondary" 
                className="w-fit mx-auto transition-transform duration-300"
                data-testid="badge-solution"
              >
                <Sparkles className="h-4 w-4 mr-2 text-primary" />
                Die Lösung
              </Badge>
              <h2 className="text-3xl lg:text-5xl font-bold text-balance font-playfair">
                Meet{" "}
                <span className="text-primary bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  LinkedIn Lina
                </span>
              </h2>
              <p className="text-xl text-muted-foreground text-pretty">
                Deine persönliche AI-Marketing-Assistentin, die 24/7 hochwertige LinkedIn-Posts erstellt - 
                automatisch, konsistent und in deinem Stil.
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {[
                {
                  icon: Bot,
                  title: "AI-Powered Content",
                  description: "Lina analysiert deine Firma, deine Zielgruppe und erstellt Posts, die perfekt zu dir passen"
                },
                {
                  icon: Zap,
                  title: "Vollautomatisch",
                  description: "Von der Idee bis zur Veröffentlichung - alles läuft im Hintergrund, während du arbeitest"
                },
                {
                  icon: Target,
                  title: "Datengestützt",
                  description: "Jeder Post basiert auf bewährten Patterns und Performance-Analytics"
                },
                {
                  icon: Calendar,
                  title: "Konsistent",
                  description: "Regelmäßige Posts, immer pünktlich - nie wieder Posting-Lücken"
                }
              ].map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100} direction="up">
                  <FloatingCard className="h-full" data-testid={`solution-feature-${index}`}>
                    <CardContent className="pt-6 space-y-3">
                      <feature.icon className="h-8 w-8 text-primary" />
                      <h3 className="text-lg font-semibold">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </FloatingCard>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection delay={400} direction="up">
              <FloatingCard className="bg-primary/5 border-primary/20" data-testid="solution-process">
                <CardContent className="pt-8">
                  <h3 className="text-xl font-semibold mb-6 text-center">So einfach geht's</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      { step: "1", text: "Onboarding in 5 Minuten - Firma und Ziele eingeben" },
                      { step: "2", text: "Lina analysiert alles und lernt deinen Stil" },
                      { step: "3", text: "Content-Autopilot läuft - du lehnst dich zurück" }
                    ].map((step, index) => (
                      <div key={index} className="text-center space-y-2">
                        <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-xl flex items-center justify-center mx-auto">
                          {step.step}
                        </div>
                        <p className="text-sm">{step.text}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </FloatingCard>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* T - TRANSFORMATION Section */}
      <section className="py-20 bg-muted/30">
        <div className="container px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection className="text-center space-y-6 mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-balance font-playfair">
                Vorher vs. Nachher
              </h2>
              <p className="text-xl text-muted-foreground text-pretty">
                So verändert LinkedIn Lina dein Content-Marketing
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-8">
              {/* VORHER */}
              <AnimatedSection delay={100} direction="left">
                <FloatingCard className="h-full bg-destructive/5 border-destructive/20" data-testid="before-card">
                  <CardContent className="pt-6 space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-destructive mb-2">❌ Vorher</h3>
                      <p className="text-sm text-muted-foreground">Ohne LinkedIn Lina</p>
                    </div>
                    <ul className="space-y-4">
                      {[
                        { label: "Zeit für Content", value: "15+ Stunden/Woche", bad: true },
                        { label: "Posting-Frequenz", value: "Unregelmäßig, Lücken", bad: true },
                        { label: "Content-Qualität", value: "Schwankend", bad: true },
                        { label: "Strategie", value: "Trial & Error", bad: true },
                        { label: "ROI-Tracking", value: "Nicht vorhanden", bad: true },
                        { label: "Stress-Level", value: "Hoch", bad: true }
                      ].map((item, index) => (
                        <li key={index} className="flex justify-between items-center text-sm border-b border-destructive/10 pb-2">
                          <span className="text-muted-foreground">{item.label}</span>
                          <span className="font-semibold text-destructive">{item.value}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </FloatingCard>
              </AnimatedSection>

              {/* NACHHER */}
              <AnimatedSection delay={200} direction="right">
                <FloatingCard className="h-full bg-primary/5 border-primary/20" data-testid="after-card">
                  <CardContent className="pt-6 space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-primary mb-2">✅ Nachher</h3>
                      <p className="text-sm text-muted-foreground">Mit LinkedIn Lina</p>
                    </div>
                    <ul className="space-y-4">
                      {[
                        { label: "Zeit für Content", value: "~30 Min/Woche", good: true },
                        { label: "Posting-Frequenz", value: "Täglich, zuverlässig", good: true },
                        { label: "Content-Qualität", value: "Konsistent hoch", good: true },
                        { label: "Strategie", value: "Datengestützt", good: true },
                        { label: "ROI-Tracking", value: "Vollständig", good: true },
                        { label: "Stress-Level", value: "Minimal", good: true }
                      ].map((item, index) => (
                        <li key={index} className="flex justify-between items-center text-sm border-b border-primary/10 pb-2">
                          <span className="text-muted-foreground">{item.label}</span>
                          <span className="font-semibold text-primary">{item.value}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </FloatingCard>
              </AnimatedSection>
            </div>

            {/* Stats */}
            <AnimatedSection delay={300} direction="up" className="mt-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: "95%", label: "Zeitersparnis" },
                  { value: "10x", label: "Mehr Posts" },
                  { value: "100%", label: "Konsistenz" },
                  { value: "24/7", label: "Autopilot" }
                ].map((stat, index) => (
                  <FloatingCard key={index} className="text-center" data-testid={`stat-card-${index}`}>
                    <CardContent className="pt-6">
                      <div className="text-3xl font-bold text-primary">{stat.value}</div>
                      <div className="text-sm text-muted-foreground mt-2">{stat.label}</div>
                    </CardContent>
                  </FloatingCard>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* O - OFFER Section */}
      <section className="py-20">
        <div className="container px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center space-y-6 mb-16">
              <Badge 
                variant="secondary" 
                className="w-fit mx-auto transition-transform duration-300"
                data-testid="badge-offer"
              >
                <Sparkles className="h-4 w-4 mr-2 text-primary" />
                Was du bekommst
              </Badge>
              <h2 className="text-3xl lg:text-5xl font-bold text-balance font-playfair">
                Dein komplettes{" "}
                <span className="text-primary">Content-Marketing System</span>
              </h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Bot,
                  title: "AI Company Intelligence",
                  features: [
                    "Automatische Firmenanalyse",
                    "Value Proposition Extraktion",
                    "Ideal Customer Profile",
                    "Kontinuierliches Learning"
                  ]
                },
                {
                  icon: LineChart,
                  title: "Post Performance Analytics",
                  features: [
                    "Multi-URL LinkedIn Scraping",
                    "Success Pattern Recognition",
                    "Content-Style Analyse",
                    "Benchmark-Tracking"
                  ]
                },
                {
                  icon: Zap,
                  title: "Multi-Source Content Processing",
                  features: [
                    "YouTube Transcripts",
                    "Blog & Artikel Scraping",
                    "HTML-zu-Markdown",
                    "Smart Content Router"
                  ]
                },
                {
                  icon: Sparkles,
                  title: "GPT-4 Content Engine",
                  features: [
                    "3 optimierte Post-Varianten",
                    "Story-based Posts",
                    "Insight-focused Content",
                    "Engagement-optimiert"
                  ]
                },
                {
                  icon: Target,
                  title: "Visual Content Creation",
                  features: [
                    "DALL-E Integration",
                    "Content-spezifische Visuals",
                    "Professional Hosting",
                    "Permanente URLs"
                  ]
                },
                {
                  icon: ThumbsUp,
                  title: "LinkedIn Publishing",
                  features: [
                    "Automatische Veröffentlichung",
                    "Performance-Tracking",
                    "ROI-Messung",
                    "Content-Freigabe-Filter"
                  ]
                }
              ].map((offer, index) => (
                <AnimatedSection key={index} delay={index * 100} direction="up">
                  <FloatingCard className="h-full" data-testid={`offer-card-${index}`}>
                    <CardContent className="pt-6 space-y-4">
                      <offer.icon className="h-8 w-8 text-primary" />
                      <h3 className="text-lg font-semibold">{offer.title}</h3>
                      <ul className="space-y-2">
                        {offer.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start space-x-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </FloatingCard>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* R - RESPONSE Section (CTA) */}
      <section className="py-20 bg-muted/30">
        <div className="container px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center">
              <FloatingCard className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20" data-testid="cta-card">
                <CardContent className="pt-12 pb-12 space-y-8">
                  <div className="space-y-4">
                    <h2 className="text-3xl lg:text-5xl font-bold font-playfair">
                      Bereit für LinkedIn auf Autopilot?
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                      Starte jetzt mit LinkedIn Lina und verwandle dein Content-Marketing in eine 
                      vollautomatische Lead-Generierungs-Maschine.
                    </p>
                  </div>

                  {/* Benefits */}
                  <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                    {[
                      { icon: CheckCircle, text: "5 Minuten Setup" },
                      { icon: CheckCircle, text: "Keine technischen Kenntnisse" },
                      { icon: CheckCircle, text: "Sofort einsatzbereit" }
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center justify-center space-x-2 text-sm">
                        <benefit.icon className="h-5 w-5 text-primary flex-shrink-0" />
                        <span className="font-medium">{benefit.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div className="flex flex-col items-center gap-4">
                    <Button 
                      size="lg" 
                      className="text-lg px-8 py-6 group transition-all duration-300"
                      onClick={() => handleGetStarted("hero")}
                      data-testid="button-cta-main"
                    >
                      Jetzt LinkedIn Lina starten
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      ⚡ In 5 Minuten einsatzbereit • Keine Kreditkarte erforderlich
                    </p>
                  </div>

                  {/* Social Proof */}
                  <div className="pt-8 border-t border-primary/20">
                    <div className="flex items-center justify-center gap-8 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium">100+ Marketing Professionals</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <LineChart className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium">10.000+ Posts generiert</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ThumbsUp className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium">95% Zeitersparnis</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </FloatingCard>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8">
        <div className="container px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2">
              <Bot className="h-6 w-6 text-primary" />
              <span className="font-semibold text-primary font-playfair">LinkedIn Lina</span>
            </div>
            <div className="text-sm text-muted-foreground">
              © 2025 LinkedIn Lina • AI-Powered Content Marketing
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
